import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Send, X } from "lucide-react";
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "../../api/client";
import { Alert, ICON } from "../../components/ui";

// Suport (owner 2026-11-03: „un chatbox în colțul din dreapta jos”): the restaurant's conversation with the Meniova
// team, docked to the bottom edge of every screen of the app (owner: „să fie jos”, not floating). Closed, it only peeks at the team's unread answers (badge);
// open, it refreshes every 10 s and marks them as read. `/app/suport` (link in the e-mails) opens it.

interface ChatMessage {
  id: string;
  from: "restaurant" | "support";
  author: string;
  body: string;
  createdAt: number;
}
interface Thread {
  messages: ChatMessage[];
  unread: number;
  status: "open" | "closed";
}

export const supportKey = (rid: string) => ["support", rid] as const;

const when = (ms: number) =>
  new Date(ms).toLocaleString("ro-RO", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

export function SupportChat({ rid }: { rid: string }) {
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  // `?suport=1` (from /app/suport) opens the chat once, then leaves the address clean.
  useEffect(() => {
    if (params.get("suport") !== "1") return;
    setOpen(true);
    const next = new URLSearchParams(params);
    next.delete("suport");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const unread = useQuery({
    queryKey: [...supportKey(rid), "unread"],
    queryFn: () => api<Thread>("GET", `/restaurants/${rid}/support?peek=1`).then((t) => t.unread),
    refetchInterval: 60_000,
    staleTime: 30_000,
    enabled: !open,
  });
  const badge = open ? 0 : (unread.data ?? 0);

  return (
    <div className="chatbox-root">
      {open ? (
        <ChatPanel
          rid={rid}
          onClose={() => {
            setOpen(false);
            void qc.invalidateQueries({ queryKey: [...supportKey(rid), "unread"] });
          }}
        />
      ) : (
        <button
          type="button"
          className="chatbox-tab"
          aria-expanded={false}
          aria-label={badge ? `Suport: ${badge} mesaje noi` : "Deschide chatul de suport"}
          onClick={() => setOpen(true)}
        >
          <MessageCircle size={18} aria-hidden="true" />
          Suport
          {badge > 0 && <span className="chatbox-badge">{badge}</span>}
        </button>
      )}
    </div>
  );
}

function ChatPanel({ rid, onClose }: { rid: string; onClose: () => void }) {
  const qc = useQueryClient();
  const thread = useQuery({
    queryKey: supportKey(rid),
    queryFn: () => api<Thread>("GET", `/restaurants/${rid}/support`),
    refetchInterval: 10_000,
  });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const end = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const count = thread.data?.messages.length ?? 0;

  useEffect(() => {
    input.current?.focus();
  }, []);
  useEffect(() => {
    if (count) end.current?.scrollIntoView({ block: "end" });
  }, [count]);

  async function send(e?: FormEvent) {
    e?.preventDefault();
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    setError("");
    try {
      const t = await api<Thread>("POST", `/restaurants/${rid}/support`, { body });
      qc.setQueryData(supportKey(rid), t);
      setText("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; Shift+Enter makes a new line; Escape closes.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send();
    } else if (e.key === "Escape") onClose();
  };

  return (
    <section className="chatbox-panel" aria-label="Chat cu echipa Meniova">
      <header className="chatbox-head">
        <div>
          <strong>Suport Meniova</strong>
          <span className="chatbox-sub">
            Îți răspundem aici și pe e-mail, de obicei în aceeași zi lucrătoare.
          </span>
        </div>
        <button type="button" className="chatbox-close" aria-label="Închide chatul" onClick={onClose}>
          <X size={20} aria-hidden="true" />
        </button>
      </header>
      <div className="chat-log chatbox-log" aria-live="polite">
        {thread.isPending && (
          <p className="muted" role="status" style={{ margin: 0 }}>
            Se încarcă conversația…
          </p>
        )}
        {thread.isError && <Alert tone="error">{thread.error.message}</Alert>}
        {thread.data && !thread.data.messages.length && (
          <p className="muted" style={{ margin: 0 }}>
            Bună! Scrie-ne orice întrebare despre meniu, import, alergeni, plăți sau facturi. Un om din echipă
            îți răspunde.
          </p>
        )}
        {thread.data?.messages.map((m) => (
          <div key={m.id} className={`chat-msg ${m.from === "restaurant" ? "mine" : "theirs"}`}>
            <span className="chat-meta">
              {m.author} · {when(m.createdAt)}
            </span>
            <span className="chat-body">{m.body}</span>
          </div>
        ))}
        <div ref={end} />
      </div>
      {error && (
        <Alert tone="error" live>
          {error}
        </Alert>
      )}
      <form className="chat-form chatbox-form" onSubmit={send}>
        <label className="sr-only" htmlFor="chat-text">
          Mesajul tău
        </label>
        <textarea
          ref={input}
          id="chat-text"
          className="field-input"
          rows={2}
          maxLength={4000}
          placeholder="Scrie mesajul…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKey}
        />
        <button
          type="submit"
          className="btn btn-primary"
          aria-label="Trimite"
          disabled={busy || !text.trim()}
        >
          <Send {...ICON} aria-hidden="true" />
        </button>
      </form>
    </section>
  );
}
